import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { distinctUntilChanged } from 'rxjs/operators';
import type { EnvironmentListItem, EnvironmentResponse } from '@zag/control/models';
import { UserScopedStorageService } from '@zag/ui';
import { ControlEnvironmentService } from './control-environment.service';

const ACTIVE_ENV_STORAGE_KEY = 'smartcapacity.control.activeEnvironment';

/**
 * Service managing the currently active environment context.
 *
 * The active environment determines which tenants are shown and which
 * settings are editable. Users can switch environments via the sidebar.
 */
@Injectable({ providedIn: 'root' })
export class ActiveEnvironmentService {
  private readonly environmentService = inject(ControlEnvironmentService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userStorage = inject(UserScopedStorageService);

  /** The slug of the currently active environment */
  private readonly _activeSlug = signal<string | null>(this.getStoredSlug());

  /** Full details of the active environment (loaded on demand) */
  private readonly _activeEnvironment = signal<EnvironmentResponse | null>(null);

  /** Loading state for the active environment detail */
  private readonly _isLoadingDetail = signal(false);

  // Public state
  readonly activeSlug = this._activeSlug.asReadonly();
  readonly activeEnvironment = this._activeEnvironment.asReadonly();
  readonly isLoadingDetail = this._isLoadingDetail.asReadonly();

  /** List of all available environments from the parent service */
  readonly environments = this.environmentService.environments;
  readonly environmentsResource = this.environmentService.environmentsResource;

  /** The active environment list item (derived from the list, not the detail) */
  readonly activeListItem = computed<EnvironmentListItem | null>(() => {
    const slug = this._activeSlug();
    if (!slug) return null;
    return this.environments().find(e => e.slug === slug) ?? null;
  });

  /** Whether an environment is currently selected */
  readonly hasActiveEnvironment = computed(() => this._activeSlug() !== null);

  constructor() {
    // Auto-select the first environment when the list changes in a way that
    // requires an update (no active slug, or the stored slug is no longer
    // valid). Expressed as an rxjs-interop subscription rather than an
    // `effect()` — the project rule reserves effect() for derived state,
    // and this is an event-style reaction to a resource load.
    toObservable(this.environments)
      .pipe(
        distinctUntilChanged((prev, next) => prev.length === next.length && prev.every((e, i) => e.slug === next[i]?.slug)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(envs => {
        if (envs.length === 0) return;
        const currentSlug = this._activeSlug();
        const storedStillValid = currentSlug && envs.some(e => e.slug === currentSlug);
        if (!currentSlug || !storedStillValid) {
          this.setActive(envs[0].slug).catch(() => {
            // Best-effort auto-select; failures (e.g. navigation cancelled) are non-fatal.
          });
        }
      });
  }

  /**
   * Set the active environment by slug.
   * Loads the environment detail and navigates to the environment dashboard.
   */
  async setActive(slug: string, navigate = true): Promise<void> {
    this._activeSlug.set(slug);
    this.saveSlugToStorage(slug);
    await this.loadEnvironmentDetail(slug);

    if (navigate) {
      await this.router.navigate(['/environments', slug]);
    }
  }

  /** Reload the environment detail for the current active environment */
  async refreshActiveEnvironment(): Promise<void> {
    const slug = this._activeSlug();
    if (slug) {
      await this.loadEnvironmentDetail(slug);
    }
  }

  /** Reload the environment list */
  reloadList(): void {
    this.environmentService.reload();
  }

  /** Load the full detail of the specified environment */
  private async loadEnvironmentDetail(slug: string): Promise<void> {
    this._isLoadingDetail.set(true);
    try {
      const detail = await this.environmentService.getEnvironment(slug);
      this._activeEnvironment.set(detail);
    } catch {
      this._activeEnvironment.set(null);
    } finally {
      this._isLoadingDetail.set(false);
    }
  }

  private getStoredSlug(): string | null {
    return this.userStorage.get(ACTIVE_ENV_STORAGE_KEY);
  }

  private saveSlugToStorage(slug: string): void {
    this.userStorage.set(ACTIVE_ENV_STORAGE_KEY, slug);
  }
}
